import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, ButtonGroup, Form, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faList, faMap, faFilter, faTimes } from '@fortawesome/free-solid-svg-icons';
import Map, { Marker, NavigationControl, Popup, MapRef } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import PropertyCard from '../common/PropertyCard';
import axiosInstance from '../../helpers/api';
import './PropertyResultsPanel.css';

interface Property {
  id: string;
  mlsNumber: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  price: number;
  bedrooms: number;
  bathrooms: number;
  squareFeet: number;
  images?: string[];
  latitude?: number;
  longitude?: number;
  features?: string[];
  listingBrokerage?: string;
  listingAgent?: string;
}

interface PropertyResultsPanelProps {
  properties: Property[];
  viewMode: 'list' | 'map';
  onViewModeChange: (mode: 'list' | 'map') => void;
  onPropertySelect: (property: Property) => void;
  searchCriteria?: any;
  onUpdateFilters?: (filters: any) => void;
  savedPropertyIds?: Set<string>;
  onToggleSave?: (property: Property) => void;
  showMapButton?: boolean;
  onViewMap?: () => void;
}

const PropertyResultsPanel: React.FC<PropertyResultsPanelProps> = ({
  properties,
  viewMode,
  onViewModeChange,
  onPropertySelect,
  searchCriteria,
  onUpdateFilters,
  savedPropertyIds = new Set(),
  onToggleSave,
  showMapButton = false,
  onViewMap
}) => {
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [mapCenter, setMapCenter] = useState({ lat: 33.4484, lng: -112.0740 }); // Phoenix default
  const [mapZoom, setMapZoom] = useState(11);
  const [mapboxToken, setMapboxToken] = useState<string>('');
  const mapRef = useRef<MapRef>(null);

  // Fetch Mapbox token on mount
  useEffect(() => {
    const fetchMapboxToken = async () => {
      try {
        const response = await axiosInstance.get('/properties/config/mapbox');
        if (response.data.mapboxToken) {
          setMapboxToken(response.data.mapboxToken);
        }
      } catch (error) {
        console.error('Failed to fetch Mapbox token:', error);
        // Fallback token if needed
        setMapboxToken('pk.eyJ1IjoiaG91c2VleiIsImEiOiJjbHQyZ2dyMm0wMGxxMmxuczR5dmlpOWxtIn0.s-vyWzYxKcc2c7ObD5gvqQ');
      }
    };
    
    fetchMapboxToken();
  }, []);

  // Fit map to property bounds when switching to map view or properties change
  const fitMapToBounds = useCallback(() => {
    if (!mapRef.current || properties.length === 0) return;
    
    const validProperties = properties.filter(p => p.latitude && p.longitude);
    if (validProperties.length === 0) return;
    
    if (validProperties.length === 1) {
      // Single property - center on it with good zoom
      mapRef.current.flyTo({
        center: [validProperties[0].longitude!, validProperties[0].latitude!],
        zoom: 15,
        duration: 1000
      });
    } else {
      // Multiple properties - fit bounds
      const lngs = validProperties.map(p => p.longitude!);
      const lats = validProperties.map(p => p.latitude!);
      
      const bounds = [
        [Math.min(...lngs), Math.min(...lats)], // Southwest
        [Math.max(...lngs), Math.max(...lats)]  // Northeast
      ];
      
      mapRef.current.fitBounds(bounds as [[number, number], [number, number]], {
        padding: { top: 50, bottom: 50, left: 50, right: 50 },
        duration: 1000,
        maxZoom: 15
      });
    }
  }, [properties]);

  // Update map when view mode changes to map
  useEffect(() => {
    if (viewMode === 'map') {
      // Small delay to ensure map is rendered
      setTimeout(fitMapToBounds, 100);
    }
  }, [viewMode, fitMapToBounds]);

  const formatPrice = (price: number): string => {
    if (price >= 1000000) {
      return `$${(price / 1000000).toFixed(2)}M`;
    }
    return `$${price.toLocaleString()}`;
  };

  return (
    <div className="property-results-panel h-100 d-flex flex-column">
      {/* Header */}
      <div className="panel-header p-3 border-bottom bg-white sticky-top">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-0">{properties.length} Properties</h5>
            <small className="text-muted">Live MLS results</small>
          </div>
          
          <ButtonGroup size="sm">
            <Button 
              variant={viewMode === 'list' ? 'primary' : 'outline-primary'}
              onClick={() => onViewModeChange('list')}
            >
              <FontAwesomeIcon icon={faList} /> List
            </Button>
            <Button 
              variant={viewMode === 'map' ? 'primary' : 'outline-primary'}
              onClick={() => onViewModeChange('map')}
            >
              <FontAwesomeIcon icon={faMap} /> Map
            </Button>
            {showMapButton && onViewMap && (
              <Button 
                variant="outline-success"
                onClick={onViewMap}
                title="View in Full Map Search"
              >
                <FontAwesomeIcon icon={faMap} /> Full Map
              </Button>
            )}
          </ButtonGroup>
        </div>

        {/* Quick Filters */}
        {searchCriteria && (
          <div className="mt-2 d-flex flex-wrap gap-2">
            {searchCriteria.city && (
              <Badge bg="secondary">{searchCriteria.city}</Badge>
            )}
            {searchCriteria.maxPrice && (
              <Badge bg="secondary">Under {formatPrice(searchCriteria.maxPrice)}</Badge>
            )}
            {searchCriteria.bedrooms && (
              <Badge bg="secondary">{searchCriteria.bedrooms}+ beds</Badge>
            )}
            {searchCriteria.features?.pool && (
              <Badge bg="secondary">Pool</Badge>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="panel-content flex-grow-1 overflow-hidden" style={{ height: 'calc(100% - 120px)' }}>
        {viewMode === 'list' ? (
          /* List View */
          <div className="property-list p-3 h-100" style={{ overflowY: 'auto', overflowX: 'hidden' }}>
            <div className="row g-3">
              {properties.map((property) => (
                <div key={property.id} className="col-12">
                  <PropertyCard
                    property={{
                      ...property,
                      mls_number: property.mlsNumber,
                      square_feet: property.squareFeet,
                      listing_brokerage: property.listingBrokerage || undefined,
                      listing_agent: property.listingAgent || undefined
                    }}
                    onView={() => onPropertySelect(property)}
                    onSave={onToggleSave ? () => onToggleSave(property) : undefined}
                    isSaved={savedPropertyIds.has(property.id)}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* Map View */
          <div className="property-map h-100">
            {mapboxToken ? (
              <Map
                ref={mapRef}
                mapboxAccessToken={mapboxToken}
              initialViewState={{
                longitude: mapCenter.lng,
                latitude: mapCenter.lat,
                zoom: mapZoom
              }}
              onMove={(evt) => {
                setMapCenter({ lat: evt.viewState.latitude, lng: evt.viewState.longitude });
                setMapZoom(evt.viewState.zoom);
              }}
              mapStyle="mapbox://styles/mapbox/streets-v11"
              style={{ width: '100%', height: '100%' }}
            >
              <NavigationControl position="top-right" />
              
              {properties.map((property) => 
                property.latitude && property.longitude ? (
                  <Marker
                    key={property.id}
                    longitude={property.longitude}
                    latitude={property.latitude}
                    anchor="bottom"
                    onClick={(e) => {
                      e.originalEvent.stopPropagation();
                      setSelectedProperty(property);
                    }}
                  >
                    <div className="map-marker">
                      <div className="marker-price">
                        {formatPrice(property.price)}
                      </div>
                    </div>
                  </Marker>
                ) : null
              )}

              {selectedProperty && selectedProperty.latitude && selectedProperty.longitude && (
                <Popup
                  longitude={selectedProperty.longitude}
                  latitude={selectedProperty.latitude}
                  anchor="bottom"
                  onClose={() => setSelectedProperty(null)}
                  closeButton={true}
                  closeOnClick={false}
                  maxWidth="300px"
                >
                  <div className="map-popup p-2">
                    <img 
                      src={selectedProperty.images?.[0] || 'https://via.placeholder.com/300x200'} 
                      alt={selectedProperty.address}
                      className="w-100 mb-2"
                      style={{ height: '150px', objectFit: 'cover' }}
                    />
                    <h6 className="mb-1">{formatPrice(selectedProperty.price)}</h6>
                    <p className="small mb-1">{selectedProperty.address}</p>
                    <div className="small text-muted mb-2">
                      {selectedProperty.bedrooms}bd • {selectedProperty.bathrooms}ba • {selectedProperty.squareFeet}sf
                    </div>
                    {selectedProperty.listingBrokerage && (
                      <div className="small text-muted mb-2 fst-italic">
                        Courtesy of {selectedProperty.listingBrokerage}
                      </div>
                    )}
                    <Button 
                      size="sm" 
                      variant="primary" 
                      className="w-100"
                      onClick={() => onPropertySelect(selectedProperty)}
                    >
                      View Details
                    </Button>
                  </div>
                </Popup>
              )}
            </Map>
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100">
                <div className="text-center">
                  <p className="text-muted">Loading map...</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer - Only show in map view */}
      {viewMode === 'map' && properties.length > 20 && (
        <div className="panel-footer p-2 border-top bg-light text-center">
          <small className="text-muted">
            Showing {properties.length} properties • Zoom in to see more details
          </small>
        </div>
      )}
    </div>
  );
};

export default PropertyResultsPanel;